"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const jwt_decode_1 = (0, tslib_1.__importDefault)(require("jwt-decode"));
const types_1 = require("./types");
(0, tslib_1.__exportStar)(require("./types"), exports);
/**
 * @description VK-IT/auth base class
 * @param {string | null} url Auth url override
 */
class VKITAuth {
    constructor(url = null) {
        this.url = types_1.VKITAuthUrl.DEV;
        this.url = url || this.url;
    }
    /**
     * @private
     * @description Makes token generation/refresh request
     * @param {Partial<VKITAuthExchangeParams | VKITAuthRefreshParams>} data Token data
     * @param {string} data.code Code variable
     * @param {string} data.token Additional special token
     * @param {string} data.provider Provider variable
     * @param {string} data.refresh Refresh variable
     * @param {string} data.state State variable
     * @returns {Promise<VKITAuthTokens>} New tokens
     * @throws {VKITAuthErrorResponse} Error response
     */
    redeemTokens({ code, provider = types_1.VKITAuthProvider.ADFS, refresh, state, token, }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            try {
                let params = `?code=${code}&state=${state}`;
                let init = {
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    method: 'POST',
                };
                if (refresh) {
                    params = `?provider=${provider}`;
                    init = Object.assign(Object.assign({}, init), { body: new URLSearchParams({ refresh_token: refresh }), headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                        } });
                }
                if (token) {
                    init.headers = Object.assign(Object.assign({}, init.headers), { Authorization: `Token ${token}` });
                }
                const response = yield fetch(`${this.url}/api/v1/auth/token/${params}`, init);
                const tokens = yield response.json();
                if ('auth_error' in tokens) {
                    throw tokens;
                }
                localStorage.setItem(types_1.VKITAuthStorage.TOKENS, JSON.stringify(tokens));
                if (provider) {
                    localStorage.setItem(types_1.VKITAuthStorage.PROVIDER, provider);
                }
                return tokens;
            }
            catch (e) {
                const { message } = e;
                if (message) {
                    throw { auth_error: message };
                }
                throw e;
            }
        });
    }
    /**
     * @private
     * @description Replaces browser history with filtered one
     * @param {URLSearchParams} params Params to apply to history
     */
    replaceHistory(params) {
        window.history.replaceState({}, document.title, window.location.pathname + (params.toString() ? `?${params.toString()}` : ''));
    }
    /**
     * @description Cleans stored tokens
     */
    clean() {
        localStorage.removeItem(types_1.VKITAuthStorage.TOKENS);
    }
    /**
     * @description Exchanges code and state for tokens
     * @async
     * @param {VKITAuthExchangeParams} params Exchange code params
     * @returns {Promise<VKITAuthTokens>} New auth tokens
     * @throws {VKITAuthErrorResponse} Error response
     */
    exchangeCode({ code = '', state = '', provider, token } = { code: '', state: '' }) {
        var _a;
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const searchParams = new URLSearchParams(window.location.search);
            const codeValue = code || searchParams.get(types_1.VKITAuthParams.CODE);
            const stateValue = state || searchParams.get(types_1.VKITAuthParams.STATE);
            const providerValue = provider || ((_a = searchParams.get(types_1.VKITAuthParams.PROVIDER)) !== null && _a !== void 0 ? _a : types_1.VKITAuthProvider.ADFS);
            const errorValue = searchParams.get(types_1.VKITAuthParams.ERROR);
            if (errorValue) {
                searchParams.delete(types_1.VKITAuthParams.ERROR);
                this.replaceHistory(searchParams);
                throw { auth_error: errorValue };
            }
            if (codeValue && stateValue && providerValue) {
                const tokens = yield this.redeemTokens({ code: codeValue, state: stateValue, provider: providerValue, token });
                searchParams.delete(types_1.VKITAuthParams.CODE);
                searchParams.delete(types_1.VKITAuthParams.STATE);
                searchParams.delete(types_1.VKITAuthParams.PROVIDER);
                this.replaceHistory(searchParams);
                return tokens;
            }
            throw { auth_error: 'No code or state value was provided' };
        });
    }
    /**
     * @description Returns current access token
     * @returns {string | null} Rehydrated access token
     */
    getAccessToken() {
        const dehydratedTokens = localStorage.getItem(types_1.VKITAuthStorage.TOKENS);
        if (dehydratedTokens) {
            const tokens = JSON.parse(dehydratedTokens);
            return tokens.access_token;
        }
        return null;
    }
    /**
     * @description Returns decoded access token
     * @template T
     * @param {string} accessToken Token to decode
     * @returns {T | null} Decoded token data
     */
    getClaims(accessToken = this.getAccessToken()) {
        if (accessToken) {
            return (0, jwt_decode_1.default)(accessToken);
        }
        return null;
    }
    /**
     * @description Returns code param if it is exists
     * @returns {string | null} State param
     */
    getCodeParam() {
        const params = new URLSearchParams(window.location.search);
        return params.get(types_1.VKITAuthParams.CODE);
    }
    /**
     * @description Returns error param if it is exists
     * @returns {string | null} Error param
     */
    getErrorParam() {
        const params = new URLSearchParams(window.location.search);
        return params.get(types_1.VKITAuthParams.ERROR);
    }
    /**
     * @description Returns provider param if it is exists
     * @returns {string | null} State param
     */
    getProviderParam() {
        const params = new URLSearchParams(window.location.search);
        return params.get(types_1.VKITAuthParams.PROVIDER);
    }
    /**
     * @description Returns state param if it is exists
     * @returns {string | null} State param
     */
    getStateParam() {
        const params = new URLSearchParams(window.location.search);
        return params.get(types_1.VKITAuthParams.STATE);
    }
    /**
     * @description Returns current provider token
     * @returns {string | null} Rehydrated provider token
     */
    getProviderToken() {
        return localStorage.getItem(types_1.VKITAuthStorage.PROVIDER);
    }
    /**
     * @description Returns current refresh token
     * @returns {string | null} Rehydrated access token
     */
    getRefreshToken() {
        const dehydratedTokens = localStorage.getItem(types_1.VKITAuthStorage.TOKENS);
        if (dehydratedTokens) {
            const tokens = JSON.parse(dehydratedTokens);
            return tokens.refresh_token;
        }
        return null;
    }
    /**
     * @description Checks whether error param exists or not
     * @returns {boolean} Check result
     */
    hasErrorParam() {
        const params = new URLSearchParams(window.location.search);
        return params.has(types_1.VKITAuthParams.ERROR);
    }
    /**
     * @description Checks whether exchange method can be called or not
     * @returns {boolean} Check result
     */
    hasNecessaryParams() {
        return this.hasSuccessParams() || this.hasErrorParam();
    }
    /**
     * @description Checks whether success params (code and state) exist or not
     * @returns {boolean} Check result
     */
    hasSuccessParams() {
        const params = new URLSearchParams(window.location.search);
        return params.has(types_1.VKITAuthParams.CODE) && params.has(types_1.VKITAuthParams.STATE);
    }
    /**
     * @description Performs logout redirect
     */
    logOut() {
        const frame = document.createElement('iframe');
        frame.src = 'https://stsmid.beeline.ru/adfs/ls/?wa=wsignout1.0';
        frame.setAttribute('style', 'position: absolute; width:0; height:0; border:0;');
        document.body.appendChild(frame);
        frame.addEventListener('load', () => {
            this.clean();
            frame.remove();
        });
    }
    /**
     * @async
     * @description Refreshes access_token
     * @param {VKITAuthRefreshParams} params Refresh params
     * @returns {Promise<VKITAuthTokens | null>} New auth tokens
     * @throws {VKITAuthErrorResponse} Error response
     */
    refresh({ provider = this.getProviderToken() || types_1.VKITAuthProvider.ADFS, refresh, token } = {
        refresh: this.getRefreshToken() || '',
    }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            if (refresh) {
                return this.redeemTokens({ provider, refresh, token });
            }
            throw { auth_error: 'No refresh token was provided' };
        });
    }
    /**
     * @description Gets Authorisation code through redirect
     * @param {VKITAuthStartParams} params Start auth params
     */
    startAuth({ impersonate, next = window.location.href, provider = types_1.VKITAuthProvider.ADFS, replaceHistory = true, } = {}) {
        const authImpersonate = impersonate ? `&impersonate=${impersonate}` : '';
        const authProvider = provider ? `&provider=${provider}` : '';
        const authParams = `?next=${encodeURIComponent(next)}${authProvider}${authImpersonate}`;
        const url = `${this.url}/api/v1/auth/login/${authParams}`;
        if (provider) {
            localStorage.setItem(types_1.VKITAuthStorage.PROVIDER, provider);
        }
        if (replaceHistory) {
            window.location.replace(url);
            return;
        }
        window.location.href = url;
    }
}
exports.default = VKITAuth;
//# sourceMappingURL=index.js.map